import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { ContactInfoStep } from '../components/SetupWizard/ContactInfoStep';
import { LocaleSettingsStep } from '../components/SetupWizard/LocaleSettingsStep';
import { CompanyProfileStep } from '../components/SetupWizard/CompanyProfileStep';
import { CompletionStep } from '../components/SetupWizard/CompletionStep';

export const SetupWizardPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepData, setStepData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const notify = useNotify();
  const navigate = useNavigate();

  // Get user role to determine if we need company profile step
  const userRole = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.userRole;
    } catch {
      return null;
    }
  }, []);

  // Build steps array conditionally based on user role
  const steps = useMemo(() => {
    const baseSteps = [
      { id: 'contact', title: 'Contact Information', component: ContactInfoStep },
    ];

    // Add company profile step for company users (after contact info, before locale)
    if (userRole === 'company') {
      baseSteps.push({
        id: 'company-profile',
        title: 'Company Profile',
        component: CompanyProfileStep,
      });
    }

    baseSteps.push(
      { id: 'locale', title: 'Locale Settings', component: LocaleSettingsStep },
      { id: 'completion', title: 'Setup Complete', component: CompletionStep }
    );

    return baseSteps;
  }, [userRole]);

  const progress = ((currentStep + 1) / steps.length) * 100;

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleNext = async (data?: any) => {
    setError('');

    try {
      // Save current step data
      if (data) {
        const updatedStepData = {
          ...stepData,
          [steps[currentStep].id]: data
        };
        setStepData(updatedStepData);
        // Also store in localStorage for CompanyProfileStep to access account address/phone
        localStorage.setItem('setupWizardData', JSON.stringify(updatedStepData));
      }

      // If this is the last step, save all data and redirect
      if (currentStep === steps.length - 1) {
        setLoading(true);

        // Merge all step data
        const allData = {
          ...stepData,
          [steps[currentStep].id]: data
        };

        // Update user with all collected data
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const token = localStorage.getItem('accessToken');
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        // Format dateOfBirth to ISO-8601 DateTime format if present
        let dateOfBirth = allData.contact?.dateOfBirth;
        if (dateOfBirth) {
          // Convert YYYY-MM-DD to YYYY-MM-DDTHH:MM:SSZ
          dateOfBirth = `${dateOfBirth}T00:00:00Z`;
        }

        // Update user with all collected data including userRole
        const updatePayload: any = {
          phoneNumber: allData.contact?.phoneNumber,
          phoneExtension: allData.contact?.phoneExtension,
          dateOfBirth: dateOfBirth,
          addressLine1: allData.contact?.addressLine1,
          addressLine2: allData.contact?.addressLine2,
          city: allData.contact?.city,
          province: allData.contact?.province,
          postalCode: allData.contact?.postalCode,
          weightUnit: allData.locale?.weightUnit,
          distanceUnit: allData.locale?.distanceUnit,
          timezone: allData.locale?.timezone,
        };

        const response = await fetch(`${baseUrl}/users/${user.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatePayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to save setup data');
        }

        // Save company profile if this is a company user
        if (userRole === 'company' && allData['company-profile']) {
          const companyProfileData = allData['company-profile'];
          console.log('Saving company profile data:', companyProfileData);
          
          // Get tenant code - try to get from user's tenants array first
          const getTenantCode = () => {
            const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';
            if (!tenantsEnabled) return 'default';
            
            // First, try to get from currentTenant in localStorage
            const currentTenant = localStorage.getItem('currentTenant');
            if (currentTenant) {
              try {
                const parsed = JSON.parse(currentTenant);
                if (parsed && parsed.code) {
                  console.log('Using tenant code from localStorage:', parsed.code);
                  return parsed.code;
                }
              } catch (e) {
                console.warn('Error parsing currentTenant from localStorage:', e);
              }
            }
            
            // If not in localStorage, try to get from user's tenants array
            if (user && user.tenants && user.tenants.length > 0) {
              // For company users, they should have exactly one tenant (the one created during registration)
              const userTenant = user.tenants[0];
              if (userTenant && userTenant.tenantCode) {
                console.log('Using tenant code from user tenants:', userTenant.tenantCode);
                // Also set it in localStorage for future use
                localStorage.setItem('currentTenant', JSON.stringify({
                  code: userTenant.tenantCode,
                  name: userTenant.tenant?.name || 'Company Tenant'
                }));
                return userTenant.tenantCode;
              }
            }
            
            console.warn('No tenant code found, using default');
            return 'default';
          };

          const tenantCode = getTenantCode();
          console.log('Using tenant code:', tenantCode);

          // Prepare company profile payload (exclude companyName as it's already set)
          const companyProfilePayload: any = {
            website: companyProfileData.website,
            linkToAccountAddress: companyProfileData.linkToAccountAddress,
            addressLine1: companyProfileData.addressLine1,
            addressLine2: companyProfileData.addressLine2,
            city: companyProfileData.city,
            province: companyProfileData.province,
            postalCode: companyProfileData.postalCode,
            countryCode: companyProfileData.countryCode,
            linkToAccountPhone: companyProfileData.linkToAccountPhone,
            phone: companyProfileData.phone,
            hasIftaLicence: companyProfileData.hasIftaLicence,
            iftaLicenceOrigin: companyProfileData.iftaLicenceOrigin,
            iftaLicenceNumber: companyProfileData.iftaLicenceNumber,
            iftaLicenceExpiryDate: companyProfileData.iftaLicenceExpiryDate
              ? `${companyProfileData.iftaLicenceExpiryDate}T00:00:00Z`
              : undefined,
            facebookUrl: companyProfileData.facebookUrl,
            twitterUrl: companyProfileData.twitterUrl,
            linkedInUrl: companyProfileData.linkedInUrl,
          };

          // Update company profile fields, which live on the Tenant record
          // (the former separate CompanyProfile table was merged into Tenant)
          console.log('Patching company profile with payload:', companyProfilePayload);
          console.log('Tenant code being used:', tenantCode);
          console.log('User tenants:', user.tenants);

          const companyProfileResponse = await fetch(`${baseUrl}/tenants/${tenantCode}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'x-tenant-code': tenantCode,
            },
            body: JSON.stringify(companyProfilePayload),
          });

          console.log('Company profile response status:', companyProfileResponse.status);
          console.log('Company profile response headers:', Object.fromEntries(companyProfileResponse.headers.entries()));

          if (!companyProfileResponse.ok) {
            let errorMessage = 'Failed to save company profile';
            let errorData: any = null;
            try {
              errorData = await companyProfileResponse.json();
              errorMessage = errorData.message || errorMessage;
              console.error('Company profile error:', errorData);
            } catch (parseError) {
              const errorText = await companyProfileResponse.text();
              console.error('Company profile error (text):', errorText);
              errorMessage = `Failed to save company profile: ${companyProfileResponse.status} ${errorText}`;
            }
            
            // If it's a 403, provide more helpful error message
            if (companyProfileResponse.status === 403) {
              errorMessage = `Permission denied. Please ensure you have access to tenant '${tenantCode}'. User tenants: ${user.tenants?.map((t: any) => t.tenantCode).join(', ') || 'none'}`;
            }
            
            throw new Error(errorMessage);
          }

          const savedProfile = await companyProfileResponse.json();
          console.log('Company profile saved successfully:', savedProfile);
        }

        // Clear setup flag and wizard data
        localStorage.removeItem('showSetupWizard');
        localStorage.removeItem('setupWizardData');

        const redirectMessage = 'Setup complete! Redirecting to your dashboard...';
        
        notify(redirectMessage, { type: 'success' });

        // Reset loading state before redirect
        setLoading(false);

        // Redirect to dashboard
        setTimeout(() => {
          try {
            console.log('Redirecting to dashboard...');
            window.location.href = '/';
          } catch (redirectError) {
            console.error('Redirect error:', redirectError);
            setError('Failed to redirect. Please navigate manually.');
            setLoading(false);
          }
        }, 1000);
      } else {
        // Move to next step
        setCurrentStep(currentStep + 1);
      }
    } catch (err: any) {
      console.error('Setup wizard error:', err);
      console.error('Error stack:', err.stack);
      setError(err.message || 'An error occurred');
      setLoading(false);
      notify(err.message || 'An error occurred during setup', { type: 'error' });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 800,
          mt: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
            ACCOUNT SETUP
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {steps[currentStep].title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              STEP {currentStep + 1} OF {steps.length}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#1976d2'
              }
            }}
          />
        </Box>

        {/* Content */}
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {CurrentStepComponent && (
            <CurrentStepComponent
              onNext={handleNext}
              onBack={handleBack}
              existingData={{
                ...stepData[steps[currentStep].id],
                // Pass contact data to company profile step so it can show account address/phone
                contactData: stepData.contact,
              }}
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};
